import type { SpecificationFiguresService } from '../specification-figures-service';
import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter } from '@patsnap/tahiti-core';
import type { Event } from '@patsnap/tahiti-core';
import type { SpecificationFigure } from '../../types/specification-figures';
import { FiguresPersistenceService } from '../figures-persistence-service';

@injectable()
export class SpecificationFiguresServiceImpl implements SpecificationFiguresService {
    private _figures: SpecificationFigure[] = [];
    private _currentIndex = 0;

    private _onCurrentIndexChangeEmitter = new Emitter<number>();
    readonly onCurrentIndexChange: Event<number> = this._onCurrentIndexChangeEmitter.event;

    private _onFiguresChangeEmitter = new Emitter<SpecificationFigure[]>();
    readonly onFiguresChange: Event<SpecificationFigure[]> = this._onFiguresChangeEmitter.event;

    constructor(
        @inject(FiguresPersistenceService)
        private _persistence: FiguresPersistenceService,
    ) {}

    get currentIndex(): number {
        return this._currentIndex;
    }

    get figures(): SpecificationFigure[] {
        return this._figures;
    }

    async loadPersistedFigures(): Promise<void> {
        this._figures = await this._persistence.loadFigures();
        this._onFiguresChangeEmitter.fire(this._figures);
    }

    removeFigure(index: number): void {
        const newFigures = this._figures.slice();
        if (index === this._currentIndex) {
            if (index === newFigures.length - 1) {
                this.setCurrentIndex(Math.max(0, this._currentIndex - 1));
            }
        } else if (index < this._currentIndex) {
            this.setCurrentIndex(this._currentIndex - 1);
        }
        newFigures.splice(index, 1);
        this.updateFigures(newFigures);
    }

    reorderFigures(newOrder: string[]): void {
        const newFigures = this._figures.slice();
        newFigures.sort((a, b) => newOrder.indexOf(a.id) - newOrder.indexOf(b.id));
        this.updateFigures(newFigures);
    }

    addFigure(figure: SpecificationFigure): void {
        const newFigures = [...this._figures, figure];
        this.updateFigures(newFigures);
    }

    setCurrentIndex(index: number): void {
        if (index !== this._currentIndex) {
            this._currentIndex = index;
            this._onCurrentIndexChangeEmitter.fire(this._currentIndex);
        }
    }

    updateFigures(images: SpecificationFigure[]): void {
        this._figures = images;
        this._onFiguresChangeEmitter.fire(this._figures);
        this._persistence.saveFigures(this._figures);
    }
}
